/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Swal from 'sweetalert2';
import MDButton from 'components/MDButton';
import MDDatePicker from 'components/MDDatePicker';
import MDTimePicker from 'components/MDTimePicker';
import { api } from '../../../api';
import {
  DROPDOWN_SX,
  TEAL,
  TIME_PICKER_STEP_MINUTES,
  formatDisplayDate,
  formatDisplayTime,
  formatTimeFor24Hour,
  getCurrentUserId,
} from '../constants';

export default function ReAppointmentDialog({ appointment, onClose, onDone }) {
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState(appointment.irm_users_id);
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState('');
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.contacts
      .getDoctorsList()
      .then(resp => setDoctors(resp?.data || []))
      .catch(() => setDoctors([]));
  }, []);

  const handleSubmit = async () => {
    if (!appointmentDate) {
      Swal.fire({ icon: 'warning', title: 'Date is required' });
      return;
    }
    const confirmResult = await Swal.fire({
      title: 'Create a re-appointment?',
      html: `A new linked appointment will be scheduled on <b>${formatDisplayDate(appointmentDate)}</b>${appointmentTime ? ` at <b>${formatDisplayTime(appointmentTime)}</b>` : ''}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: TEAL,
      confirmButtonText: 'Yes, Create',
      cancelButtonText: 'Cancel',
    });
    if (!confirmResult.isConfirmed) return;

    setSubmitting(true);
    try {
      await api.appointment.createReAppointment(appointment.id, {
        appointment_date: appointmentDate,
        appointment_time: formatTimeFor24Hour(appointmentTime),
        irm_users_id: doctorId || undefined,
        reason_for_visit: reasonForVisit || undefined,
        created_by: getCurrentUserId(),
      });
      Swal.fire({ icon: 'success', title: 'Re-appointment created', timer: 1800, showConfirmButton: false });
      onDone?.();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create Re-Appointment</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="reappt-doctor-label">Doctor</InputLabel>
            <Select
              labelId="reappt-doctor-label"
              label="Doctor"
              value={doctorId || ''}
              onChange={e => setDoctorId(e.target.value)}
              sx={DROPDOWN_SX}
            >
              {doctors.map(d => (
                <MenuItem key={d.user_id} value={d.user_id}>
                  {d.name} {d.occupation ? `(${d.occupation})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <MDDatePicker label="Appointment Date" value={appointmentDate} onChange={setAppointmentDate} />
            <MDTimePicker
              label="Time (optional)"
              value={appointmentTime}
              onChange={val => setAppointmentTime(val || '')}
              minutesStep={TIME_PICKER_STEP_MINUTES}
              textFieldProps={{ fullWidth: true }}
            />
          </Box>
          <TextField
            label="Reason for visit (optional)"
            size="small"
            fullWidth
            value={reasonForVisit}
            onChange={e => setReasonForVisit(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">Cancel</MDButton>
        <MDButton onClick={handleSubmit} disabled={submitting} variant="gradient" color="logo">
          {submitting ? 'Creating…' : 'Create'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
